import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Activity, DollarSign } from "lucide-react";
import { getDashboardKpis } from "@/services/apiCallsService";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";

interface StatsCardsProps {
  clientId?: string;
  workspaceId?: string;
  month?: number;
  year?: number;
}

export const StatsCards = ({ clientId, month, year }: StatsCardsProps = {}) => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { isAdminOrSupport } = usePermissions();

  useEffect(() => {
    loadKpis();
  }, [clientId, month, year]);

  const loadKpis = async () => {
    try {
      setLoading(true);
      setError(null);
      const currentDate = new Date();
      const currentMonth = month || currentDate.getMonth() + 1;
      const currentYear = year || currentDate.getFullYear();

      // Para sandbox, client_id deve ser null
      const clientIdParam =
        clientId === "null" || clientId === null
          ? "null"
          : clientId && clientId !== "all"
            ? clientId
            : undefined;

      const response = await getDashboardKpis({
        month: currentMonth,
        year: currentYear,
        client_id: clientIdParam,
      });

      if (response.success && response.data) {
        setKpis(response.data);
      } else {
        const message = response.error?.message || "Erro ao carregar KPIs";
        setKpis(null);
        setError(message);
        toast({
          title: "Erro",
          description: message,
          variant: "destructive",
        });
      }
    } catch {
      const message = "Erro ao carregar KPIs do dashboard";
      setKpis(null);
      setError(message);
      toast({
        title: "Erro",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-muted rounded"></div>
              <div className="h-4 w-4 bg-muted rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 w-32 bg-muted rounded mb-2"></div>
              <div className="h-4 w-40 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !kpis) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="col-span-full">
          <CardContent className="flex items-center justify-center py-10">
            <p className="text-sm text-destructive">
              Não foi possível carregar os KPIs do dashboard.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getTrendValue = (variation: number) => {
    const sign = variation >= 0 ? "+" : "";
    return `${sign}${variation.toFixed(1)}%`;
  };

  const formatProviderCost = (value: number) => {
    return `$${Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6,
      useGrouping: true,
    })}`;
  };

  const stats = [
    {
      title: "Chamadas Totais",
      value: kpis.chamadas_totais.value.toLocaleString(),
      description: `${getTrendValue(kpis.chamadas_totais.variation)} em relação ao mês passado`,
      icon: Activity,
      trend: kpis.chamadas_totais.variation >= 0 ? "up" : "down",
      color: "text-primary",
    },
    {
      title: "Tokens Consumidos",
      value: kpis.tokens_consumidos.value.toLocaleString(),
      description: `${getTrendValue(kpis.tokens_consumidos.variation)} em relação ao mês passado`,
      icon: TrendingUp,
      trend: kpis.tokens_consumidos.variation >= 0 ? "up" : "down",
      color: "text-success",
    },
    ...(isAdminOrSupport
      ? [
          {
            title: "Custo do Provedor",
            value: formatProviderCost(Number(kpis.custo_total.value || 0)),
            description: `${getTrendValue(kpis.custo_total.variation)} em relação ao mês passado`,
            icon: DollarSign,
            trend: kpis.custo_total.variation >= 0 ? "up" : "down",
            color: "text-warning",
          },
        ]
      : []),
    {
      title: "Taxa de Erro",
      value: `${kpis.taxa_erro.value.toFixed(1)}%`,
      description: `${getTrendValue(kpis.taxa_erro.variation)} em relação ao mês passado`,
      icon: TrendingDown,
      trend: kpis.taxa_erro.variation >= 0 ? "down" : "up",
      color: "text-destructive",
    },
  ];

  const gridCols =
    stats.length >= 4
      ? "md:grid-cols-2 lg:grid-cols-4"
      : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <div className={`grid gap-4 ${gridCols}`}>
      {stats.map((stat) => (
        <Card
          key={stat.title}
          className="hover:shadow-glow transition-shadow duration-300"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
            <stat.icon className={`h-4 w-4 ${stat.color}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <p
              className={`text-xs ${
                stat.trend === "up" ? "text-success" : "text-destructive"
              }`}
            >
              {stat.description}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
